import { getValidatedLimitParam, getValidatedPageParam, useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'ums/v1/cms/bnpl/supplier-statements';
export const getSupplierStatementsEndpointIdentifier = endpoint;

export interface SupplierStatementDto {
  id: number;
  facilityId: number;
  restaurantId: number;
  supplierId: number;
  statementRef: string | null;
  statementAmount: number;
  currencyCode: string;
  periodStart: string;
  periodEnd: string;
  documentUrl: string;
  originalFilename: string;
  notes: string | null;
  createdBy: number;
  createdAt: string;
  creditFacility?: {
    id: number;
    creditLimit: number;
    utilizedAmount: number;
    reservedAmount: number;
    availableAmount: number;
  };
  restaurant?: { id: number; nameEn: string };
  supplier?: { id: number; name: string };
}

interface ResponseData {
  data: SupplierStatementDto[];
  count: number;
  total: number;
  page: number;
  pageCount: number;
  hasNext: boolean;
  hasPrev: boolean;
}

type TData = ResponseData;
type TError = AxiosError;
type TQueryKey = [typeof endpoint, { page: TParam; limit: TParam; facilityId: TParam; supplierId: TParam; restaurantId: TParam; search: TParam }];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetSupplierStatementsApi(options?: Options): ReturnType {
  const toast = useCustomToast();
  const page = getValidatedPageParam();
  const limit = getValidatedLimitParam();
  const isLoggedIn = useIsLoggedIn();
  const { getPageParams } = usePageParams();
  const { facilityId, supplierId, restaurantId, search } = getPageParams();

  const queryFn = async () => {
    try {
      const { data } = await http.get(endpoint, {
        params: { page, limit, facilityId, supplierId, restaurantId, search },
      });
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useQueryWrapper([endpoint, { page, limit, facilityId, supplierId, restaurantId, search }], queryFn, {
    enabled: isLoggedIn,
    ...options,
  });
}
